import ABDMIndex from './components/ABDMIndex';
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PatientAuthProvider, usePatientAuth } from "@/contexts/PatientAuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import Gateway from "@/pages/Gateway";
import AdminLogin from "@/pages/AdminLogin";
import DoctorLogin from "@/pages/DoctorLogin";
import DoctorProfileSetup from "@/pages/DoctorProfileSetup";
import AdminDashboard from "@/pages/AdminDashboard";
import AnalyticsPage from "@/pages/AnalyticsPage";
import DoctorDashboard from "@/pages/DoctorDashboard";
import SuperAdminLogin from "@/pages/SuperAdminLogin";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import PatientPortal from "@/pages/patient/PatientPortal";
import PatientLogin from "@/pages/patient/PatientLogin";
import PatientHomeMyBookings from "@/pages/patient/PatientHomeMyBookings";
import PatientSearch from "@/pages/patient/PatientHome"; // rename: this is the search page
import PatientDoctorProfile from "@/pages/patient/PatientDoctorProfile";
import PatientPay from "@/pages/patient/PatientPay";
import PatientSlip from "@/pages/patient/PatientSlip";
import PatientDashboard from "@/pages/patient/PatientDashboard";
import PatientPrescriptions from "@/pages/patient/PatientPrescriptions";
import PublicDoctorSharePage from "@/pages/PublicDoctorSharePage";
import PublicHospitalSharePage from "@/pages/PublicHospitalSharePage";
import SuperAdminPayoutsManagement from "@/pages/SuperAdminPayoutsManagement";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsAndConditions from "@/pages/legal/TermsAndConditions";
import ReceptionistLogin from "@/pages/ReceptionistLogin";
import ReceptionistDashboard from "@/pages/ReceptionistDashboard";
import ReceptionistIPD from "@/pages/ReceptionistIPD";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function PatientProtected({ children }) {
  const { user, loading } = usePatientAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/patient/login" replace />;
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && (user.role === "hospital_admin" || user.role === "clinic_admin")) return <Navigate to="/admin/dashboard" replace />;
  if (user && user.role === "superadmin") return <Navigate to="/superadmin" replace />;
  if (user && user.role === "doctor") return <Navigate to={user.profile_complete ? "/doctor/dashboard" : "/doctor/profile-setup"} replace />;
  if (user && user.role === "receptionist") return <Navigate to="/receptionist/dashboard" replace />;
  // B2C-first: unauthenticated visitors land on the patient portal by default.
  // Doctor / hospital / clinic / receptionist onboarding remains reachable via
  // `/for-doctors` (Gateway) or the direct `/login/{role}` routes.
  return <Navigate to="/patient" replace />;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <PatientAuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/for-doctors" element={<Gateway />} />
              <Route path="/dr/:slug" element={<PublicDoctorSharePage />} />
              <Route path="/hospital/:slug" element={<PublicHospitalSharePage />} />
              <Route path="/login/doctor" element={<DoctorLogin />} />
              <Route path="/login/:type" element={<AdminLogin />} />
              <Route path="/for-doctors" element={<Gateway />} />
              <Route path="/super-admin/login" element={<SuperAdminLogin />} />
              <Route path="/abdm" element={<ABDMIndex />} />

              {/* Patient Portal (standalone app) */}
              <Route path="/patient" element={<PatientPortal />} />
              <Route path="/patient/login" element={<PatientLogin />} />
              <Route path="/patient/search" element={<PatientSearch />} />
              <Route path="/patient/doctor/:doctorId" element={<PatientDoctorProfile />} />
              <Route path="/patient/book/:appointmentId/pay" element={<PatientPay />} />
              <Route path="/patient/slip/:appointmentId" element={<PatientSlip />} />
              <Route path="/patient/home" element={<PatientProtected><PatientHomeMyBookings /></PatientProtected>} />
              <Route path="/patient/home-new" element={<PatientProtected><PatientDashboard /></PatientProtected>} />
              <Route path="/patient/prescriptions" element={<PatientProtected><PatientPrescriptions /></PatientProtected>} />
             
              <Route path="/doctor/profile-setup" element={<Protected roles={["doctor"]}><DoctorProfileSetup /></Protected>} />
              <Route path="/doctor/dashboard" element={<Protected roles={["doctor"]}><DoctorDashboard /></Protected>} />
              <Route path="/admin/dashboard" element={<Protected roles={["hospital_admin", "clinic_admin"]}><AdminDashboard /></Protected>} />
              <Route path="/admin/analytics" element={<Protected roles={["hospital_admin", "clinic_admin"]}><AnalyticsPage /></Protected>} />
              <Route path="/superadmin" element={<Protected roles={["superadmin"]}><SuperAdminDashboard /></Protected>} />
              <Route path="/superadmin/payouts-management" element={<Protected roles={["superadmin"]}><SuperAdminPayoutsManagement /></Protected>} />
              <Route path="/legal/privacy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms" element={<TermsAndConditions />} />
              <Route path="/receptionist/login" element={<ReceptionistLogin />} />
              <Route path="/receptionist/dashboard" element={<Protected roles={["receptionist"]}><ReceptionistDashboard /></Protected>} />
              <Route path="/receptionist/ipd" element={<Protected roles={["receptionist"]}><ReceptionistIPD /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </PatientAuthProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
